import { createContext, useEffect, useState } from "react";

const CART_STORAGE_KEY = "ecm-cart-items";

const loadCartFromStorage = () => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CartContext = createContext({
  isCartOpen: false,
  setIsCartOpen: () => {},
  cartItems: [],
  addItemToCart: () => {},
  clearCart: () => {},
});

const addProductCart = (cartItems, productToAdd) => {
  let existingCartItem = cartItems.find(
    (cartItem) => cartItem.id === productToAdd.id
  );
  if (existingCartItem) {
    return cartItems.map((cartItem) =>
      cartItem.id === productToAdd.id
        ? { ...cartItem, quantity: cartItem.quantity + 1 }
        : cartItem
    );
  }
  return [...cartItems, { ...productToAdd, quantity: 1 }];
};

const removeItemCart = (cartItems, productToRemove) => {
  let existingItem = cartItems.find(
    (cartItem) => cartItem.id === productToRemove.id
  );

  if (!existingItem) return cartItems;

  if (existingItem.quantity === 1) {
    return cartItems.filter(
      (cartItem) => cartItem.id !== productToRemove.id
    );
  }

  return cartItems.map((cartItem) =>
    cartItem.id === productToRemove.id
      ? { ...cartItem, quantity: cartItem.quantity - 1 }
      : cartItem
  );
};

const clearCartItem = (cartItems, itemToClear) => {
  return cartItems.filter((cartItem) => cartItem.id !== itemToClear.id);
};

const CartProvider = ({ children }) => {
  const [isCartOpen, setIsCartOpen] = useState(false);

  const [cartItems, setCartItems] = useState(loadCartFromStorage);
  const [count, setCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch {
      // Storage may be unavailable or full
    }
  }, [cartItems]);

  useEffect(() => {
    let countValue = cartItems.reduce((total, cartItem) => {
      return total + cartItem.quantity;
    }, 0);
    setCount(countValue);
  }, [cartItems]);

  useEffect(() => {
    let totalValue = cartItems.reduce((total, cartItem) => {
      return total + cartItem.quantity * cartItem.price;
    }, 0);
    setCartTotal(totalValue);
  }, [cartItems]);

  const addItemToCart = (productToAdd) => {
    setCartItems(addProductCart(cartItems, productToAdd));
  };

  const removeItemFromCart = (productToRemove) => {
    setCartItems(removeItemCart(cartItems, productToRemove));
  };

  const clearItemFromCart = (itemToClear) => {
    setCartItems(clearCartItem(cartItems, itemToClear));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const value = {
    isCartOpen,
    setIsCartOpen,
    addItemToCart,
    cartItems,
    removeItemFromCart,
    clearItemFromCart,
    clearCart,
    count,
    cartTotal,
  };

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
};

export default CartProvider;
